import type { EventPayload } from '@side/analytics';

export enum TrackCoverSheetTaggingEvent {
  FieldBlur,
  FieldBlurWithChanges,
  ClickListingPriceCancelButton,
  ClickListingPriceSaveButton,
  EnterListingPriceSave,
  ClickContactAutocomplete,
  ClickAddressAutocomplete,
  ClickAddressAutofillStateError,
  ClickMissingFieldsView,
  ClickNavigateToMissingField,
  ClickComplianceReviewButton,
  UpdateDatesCardFromDocumentViewer,
  TooltipHover,
  ClickComplianceDualAgencyConfirmButton,
  DualAgencyModalMount,
  DualAgencyGoToListingsButton,
  DualAgencyModalExit,
  LandingOnTheCoverSheetPage,
  ClickTenantIsSelfRepresentedCheckbox,
  SelfRepresentationModalMount,
  SelfRepresentationModalExit,
  ClickSelfRepresentationConfirmButton,
  RepresentationTypeHover,
  ClickRepresentationType,
  UpdateRepresentationModalMount,
  UpdateRepresentationModalExit,
  ClickChangeRepresentationButton,
  ChangeTransactionOwner,
  ClickAddAContact,
  ClickRemoveAContact,
  ClickOpenCardIcon,
  ClickCloseCardTopRightIcon,
  ClickCloseCardButton,
  ClickExportTransactionButton,
  ClickSavingGoogleContact,
  ClickUpdatingGoogleContact,
  ClickListingPriceHistoryItemDeleteConfirmation,
  ClickListingPriceHistoryItemRestoreConfirmation,
  ClickSyncContact,
  ClickNonCoverSheetState,
  ClickCoverSheetMLSVendor,
  ClickCoverSheetMLSAutofill,
  MLSAutofillStateMismatch,
  MLSDataCompleteness,
  ClickMobileMissingFieldsShow,
  ClickMobileMissingFieldsHide,
}

type EventTypeLabels = Record<TrackCoverSheetTaggingEvent, string>;

export const AnalyticsEventLabels: EventTypeLabels = {
  [TrackCoverSheetTaggingEvent.FieldBlur]: 'COVERSHEET',
  [TrackCoverSheetTaggingEvent.FieldBlurWithChanges]: 'COVERSHEET',
  [TrackCoverSheetTaggingEvent.ClickListingPriceCancelButton]: 'COVERSHEET',
  [TrackCoverSheetTaggingEvent.ClickListingPriceSaveButton]: 'COVERSHEET',
  [TrackCoverSheetTaggingEvent.EnterListingPriceSave]: 'COVERSHEET',
  [TrackCoverSheetTaggingEvent.ClickContactAutocomplete]: 'COVERSHEET',
  [TrackCoverSheetTaggingEvent.ClickAddressAutocomplete]: 'COVERSHEET',
  [TrackCoverSheetTaggingEvent.ClickAddressAutofillStateError]: 'COVERSHEET',
  [TrackCoverSheetTaggingEvent.ClickMissingFieldsView]: 'SIDEBAR',
  [TrackCoverSheetTaggingEvent.ClickNavigateToMissingField]: 'SIDEBAR',
  [TrackCoverSheetTaggingEvent.UpdateDatesCardFromDocumentViewer]: 'COVERSHEET',
  [TrackCoverSheetTaggingEvent.ClickComplianceReviewButton]:
    'compliance_review_entry',
  [TrackCoverSheetTaggingEvent.TooltipHover]: 'COVERSHEET',
  [TrackCoverSheetTaggingEvent.ClickComplianceDualAgencyConfirmButton]:
    'COVERSHEET',
  [TrackCoverSheetTaggingEvent.DualAgencyModalMount]: 'COVERSHEET',
  [TrackCoverSheetTaggingEvent.DualAgencyGoToListingsButton]: 'COVERSHEET',
  [TrackCoverSheetTaggingEvent.DualAgencyModalExit]: 'COVERSHEET',
  [TrackCoverSheetTaggingEvent.LandingOnTheCoverSheetPage]: 'COVERSHEET',
  [TrackCoverSheetTaggingEvent.ClickTenantIsSelfRepresentedCheckbox]:
    'COVERSHEET',
  [TrackCoverSheetTaggingEvent.SelfRepresentationModalMount]: 'COVERSHEET',
  [TrackCoverSheetTaggingEvent.SelfRepresentationModalExit]: 'COVERSHEET',
  [TrackCoverSheetTaggingEvent.ClickSelfRepresentationConfirmButton]:
    'COVERSHEET',
  [TrackCoverSheetTaggingEvent.RepresentationTypeHover]: 'COVERSHEET',
  [TrackCoverSheetTaggingEvent.ClickRepresentationType]: 'COVERSHEET',
  [TrackCoverSheetTaggingEvent.UpdateRepresentationModalMount]: 'COVERSHEET',
  [TrackCoverSheetTaggingEvent.UpdateRepresentationModalExit]: 'COVERSHEET',
  [TrackCoverSheetTaggingEvent.ClickChangeRepresentationButton]: 'COVERSHEET',
  [TrackCoverSheetTaggingEvent.ChangeTransactionOwner]: 'COVERSHEET',
  [TrackCoverSheetTaggingEvent.ClickAddAContact]: 'COVERSHEET',
  [TrackCoverSheetTaggingEvent.ClickRemoveAContact]: 'COVERSHEET',
  [TrackCoverSheetTaggingEvent.ClickOpenCardIcon]: 'COVERSHEET',
  [TrackCoverSheetTaggingEvent.ClickCloseCardTopRightIcon]: 'COVERSHEET',
  [TrackCoverSheetTaggingEvent.ClickCloseCardButton]: 'COVERSHEET',
  [TrackCoverSheetTaggingEvent.ClickExportTransactionButton]: 'COVERSHEET',
  [TrackCoverSheetTaggingEvent.ClickSavingGoogleContact]: 'COVERSHEET',
  [TrackCoverSheetTaggingEvent.ClickUpdatingGoogleContact]: 'COVERSHEET',
  [TrackCoverSheetTaggingEvent.ClickListingPriceHistoryItemDeleteConfirmation]:
    'COVERSHEET',
  [TrackCoverSheetTaggingEvent.ClickListingPriceHistoryItemRestoreConfirmation]:
    'COVERSHEET',
  [TrackCoverSheetTaggingEvent.ClickSyncContact]: 'COVERSHEET',
  [TrackCoverSheetTaggingEvent.ClickNonCoverSheetState]: 'COVERSHEET',
  [TrackCoverSheetTaggingEvent.ClickCoverSheetMLSVendor]: 'COVERSHEET',
  [TrackCoverSheetTaggingEvent.ClickCoverSheetMLSAutofill]: 'COVERSHEET',
  [TrackCoverSheetTaggingEvent.MLSAutofillStateMismatch]: 'COVERSHEET',
  [TrackCoverSheetTaggingEvent.MLSDataCompleteness]: 'COVERSHEET',
  [TrackCoverSheetTaggingEvent.ClickMobileMissingFieldsShow]: 'COVERSHEET',
  [TrackCoverSheetTaggingEvent.ClickMobileMissingFieldsHide]: 'COVERSHEET',
};

type EventTypePayloads = Record<TrackCoverSheetTaggingEvent, EventPayload>;

export const AnalyticsEventsMap: EventTypePayloads = {
  [TrackCoverSheetTaggingEvent.FieldBlur]: {
    eventContainer: 'card', // card
    eventTarget: 'field_name', // dynamic
    task: 'update_input',
    eventType: 'blur',
  },
  [TrackCoverSheetTaggingEvent.ClickListingPriceCancelButton]: {
    eventContainer: 'listing_price_card',
    eventTarget: 'cancel_button',
    task: 'cancel_price_change',
    eventType: 'click',
  },
  [TrackCoverSheetTaggingEvent.ClickListingPriceSaveButton]: {
    eventContainer: 'listing_price_card',
    eventTarget: 'save_button',
    task: 'save_price_change',
    eventType: 'click',
  },
  [TrackCoverSheetTaggingEvent.EnterListingPriceSave]: {
    eventContainer: 'listing_price_card',
    eventTarget: 'save_button',
    task: 'save_price_change',
    eventType: 'keypress_enter',
  },
  [TrackCoverSheetTaggingEvent.ClickContactAutocomplete]: {
    eventContainer: '[contactType]_card',
    eventTarget: 'contact_autocomplete',
    task: 'contact_autocomplete',
    eventType: 'click',
  },
  [TrackCoverSheetTaggingEvent.ClickAddressAutocomplete]: {
    eventContainer: '[contactType]_card',
    eventTarget: 'address_autocomplete_field',
    task: 'choose_address',
    eventType: 'click',
  },
  [TrackCoverSheetTaggingEvent.ClickAddressAutofillStateError]: {
    eventContainer: 'property_card',
    eventTarget: 'street1',
    task: 'autofill_address_state_error',
    eventType: 'click',
  },
  [TrackCoverSheetTaggingEvent.ClickMissingFieldsView]: {
    eventContainer: 'missing_fields_modal',
    eventTarget: 'missing_field_view',
    eventType: 'click',
    task: 'missing_field_view',
  },
  [TrackCoverSheetTaggingEvent.ClickNavigateToMissingField]: {
    eventContainer: 'missing_fields_modal',
    eventTarget: 'missing_field_select',
    eventType: 'click',
    task: 'missing_field_selection',
  },
  [TrackCoverSheetTaggingEvent.ClickComplianceReviewButton]: {
    eventContainer: 'coversheet',
    eventTarget: 'enter_compliance_review_button',
    eventType: 'click',
    task: 'navigate',
  },
  [TrackCoverSheetTaggingEvent.FieldBlurWithChanges]: {
    eventContainer: 'card_name', // dynamically set tracking
    eventTarget: 'field_name', // dynamically set tracking
    eventType: 'blur',
    task: 'modify_input',
  },
  [TrackCoverSheetTaggingEvent.UpdateDatesCardFromDocumentViewer]: {
    eventContainer: 'important_dates_card',
    eventTarget: 'field_name', // dynamically set tracking
    eventType: 'click',
    task: 'update',
  },
  [TrackCoverSheetTaggingEvent.TooltipHover]: {
    eventContainer: 'card', // dynamic
    eventTarget: 'field_name', // dynamic
    eventType: 'hover',
    task: 'view',
  },
  [TrackCoverSheetTaggingEvent.ClickComplianceDualAgencyConfirmButton]: {
    eventContainer: 'double_ended_transaction_modal',
    eventTarget: 'continue_to_compliance_button',
    eventType: 'click',
    task: 'submit',
  },
  [TrackCoverSheetTaggingEvent.DualAgencyModalMount]: {
    eventContainer: 'coversheet',
    eventTarget: 'double_ended_transaction_modal',
    eventType: 'mount',
    task: 'view',
  },
  [TrackCoverSheetTaggingEvent.DualAgencyGoToListingsButton]: {
    eventContainer: 'double_ended_transaction_modal',
    eventTarget: 'go_to_listings_button',
    eventType: 'unmount',
    task: 'exit',
  },
  [TrackCoverSheetTaggingEvent.DualAgencyModalExit]: {
    eventContainer: 'double_ended_transaction_modal',
    eventTarget: 'background',
    eventType: 'unmount',
    task: 'exit',
  },
  [TrackCoverSheetTaggingEvent.LandingOnTheCoverSheetPage]: {
    eventContainer: 'coversheet',
    eventTarget: 'coversheet',
    eventType: 'load',
    task: 'view',
  },
  [TrackCoverSheetTaggingEvent.ClickTenantIsSelfRepresentedCheckbox]: {
    eventContainer: 'tenant_agent_card',
    eventTarget: 'self_representation_checkbox',
    eventType: 'click',
    task: 'select', // dynamic
  },
  [TrackCoverSheetTaggingEvent.SelfRepresentationModalMount]: {
    eventContainer: 'self_representation_modal',
    eventTarget: 'self_representation_modal',
    eventType: 'load',
    task: 'view',
  },
  [TrackCoverSheetTaggingEvent.SelfRepresentationModalExit]: {
    eventContainer: 'self_representation_modal',
    eventTarget: 'background', // dynamic
    eventType: 'click',
    task: 'exit',
  },
  [TrackCoverSheetTaggingEvent.ClickSelfRepresentationConfirmButton]: {
    eventContainer: 'self_representation_modal',
    eventTarget: 'self_representation_button',
    eventType: 'click',
    task: 'select',
  },
  [TrackCoverSheetTaggingEvent.RepresentationTypeHover]: {
    eventContainer: 'representation_type_card',
    eventTarget: 'landlord', // dynamic
    eventType: 'hover',
    task: 'view',
  },
  [TrackCoverSheetTaggingEvent.ClickRepresentationType]: {
    eventContainer: 'representation_type_card',
    eventTarget: 'landlord', // dynamic
    eventType: 'click',
    task: 'select',
  },
  [TrackCoverSheetTaggingEvent.UpdateRepresentationModalMount]: {
    eventContainer: 'update_representation_modal',
    eventTarget: 'update_representation_modal',
    eventType: 'load',
    task: 'view',
  },
  [TrackCoverSheetTaggingEvent.UpdateRepresentationModalExit]: {
    eventContainer: 'update_representation_modal',
    eventTarget: 'background', // dynamic
    eventType: 'click',
    task: 'exit',
  },
  [TrackCoverSheetTaggingEvent.ClickChangeRepresentationButton]: {
    eventContainer: 'update_representation_modal',
    eventTarget: 'change_representation_button',
    eventType: 'click',
    task: 'select',
  },
  [TrackCoverSheetTaggingEvent.ChangeTransactionOwner]: {
    eventContainer: 'agents_card',
    eventTarget: 'select_agent_field',
    task: 'change_transaction_owner',
    eventType: 'click',
  },
  [TrackCoverSheetTaggingEvent.ClickCloseCardTopRightIcon]: {
    eventContainer: '[contactType]_card',
    eventTarget: 'close_card_icon',
    task: 'close_card',
    eventType: 'click',
  },
  [TrackCoverSheetTaggingEvent.ClickCloseCardButton]: {
    eventContainer: '[contactType]_card',
    eventTarget: 'contact_action_button_close',
    task: 'close_card',
    eventType: 'click',
  },
  [TrackCoverSheetTaggingEvent.ClickAddAContact]: {
    eventContainer: '[contactType]_section',
    eventTarget: 'add_button',
    task: 'add_contact',
    eventType: 'click',
  },
  [TrackCoverSheetTaggingEvent.ClickRemoveAContact]: {
    eventContainer: '[contactType]_card',
    eventTarget: 'contact_action_button_remove',
    task: 'remove_contact',
    eventType: 'click',
  },
  [TrackCoverSheetTaggingEvent.ClickOpenCardIcon]: {
    eventContainer: '[contactType]_card',
    eventTarget: 'open_card_icon',
    task: 'open_card',
    eventType: 'click',
  },
  [TrackCoverSheetTaggingEvent.ClickExportTransactionButton]: {
    eventContainer: 'transaction_coversheet_header',
    eventTarget: 'export_button',
    task: 'export_data',
    eventType: 'click',
  },
  [TrackCoverSheetTaggingEvent.ClickSavingGoogleContact]: {
    eventContainer: '[contactType]_card',
    eventTarget: 'contact_action_button_update_contact',
    task: 'save_contact',
    eventType: 'click',
  },
  [TrackCoverSheetTaggingEvent.ClickUpdatingGoogleContact]: {
    eventContainer: '[contactType]_card',
    eventTarget: 'contact_action_button_update_contact',
    task: 'update_contact',
    eventType: 'click',
  },
  [TrackCoverSheetTaggingEvent.ClickListingPriceHistoryItemDeleteConfirmation]:
    {
      eventContainer: 'listing_price_confirmation_modal',
      eventTarget: 'delete_button',
      task: 'delete_price',
      eventType: 'click',
    },
  [TrackCoverSheetTaggingEvent.ClickListingPriceHistoryItemRestoreConfirmation]:
    {
      eventContainer: 'listing_price_confirmation_modal',
      eventTarget: 'restore_button',
      task: 'restore_price',
      eventType: 'click',
    },
  [TrackCoverSheetTaggingEvent.ClickSyncContact]: {
    eventContainer: '[contactType]_card',
    eventTarget: 'sync_contact_icon',
    task: 'sync_contact',
    eventType: 'click',
  },
  [TrackCoverSheetTaggingEvent.ClickNonCoverSheetState]: {
    eventContainer: 'property_card',
    eventTarget: 'property.street1',
    eventType: 'click',
    task: 'autofill_address_state_error',
  },
  [TrackCoverSheetTaggingEvent.ClickCoverSheetMLSVendor]: {
    eventContainer: 'property_card',
    eventTarget: 'mls_vendor_select',
    eventType: 'click',
    task: 'change_mls_vendor',
  },
  [TrackCoverSheetTaggingEvent.ClickCoverSheetMLSAutofill]: {
    eventContainer: 'property_card',
    eventTarget: 'mls_autofill_button',
    eventType: 'click',
    task: 'mls_autofill',
  },
  [TrackCoverSheetTaggingEvent.MLSAutofillStateMismatch]: {
    eventContainer: 'property_card',
    eventTarget: 'mls_autofill_state_mismatch',
    eventType: 'click',
    task: 'mls_autofill',
  },
  [TrackCoverSheetTaggingEvent.MLSDataCompleteness]: {
    eventContainer: 'property_card',
    eventTarget: 'mls_autofill_data_Completeness',
    eventType: 'click',
    task: 'mls_autofill',
  },
  [TrackCoverSheetTaggingEvent.ClickMobileMissingFieldsShow]: {
    eventContainer: 'missing_fields_modal',
    eventTarget: 'show_button',
    eventType: 'click',
    task: 'expand',
  },
  [TrackCoverSheetTaggingEvent.ClickMobileMissingFieldsHide]: {
    eventContainer: 'missing_fields_modal',
    eventTarget: 'hide_button',
    eventType: 'click',
    task: 'collapse',
  },
};
